import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { searchClassesTypeahead } from '@/features/search/classApi';
import type { SearchClassResult } from '@/features/search/types';

const DEFAULT_DEBOUNCE_MS = 250;
const MIN_QUERY_LENGTH = 1;

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

export interface UseClassTypeaheadOptions {
  readonly query: string;
  readonly debounceMs?: number;
  readonly enabled?: boolean;
}

export interface UseClassTypeaheadResult {
  readonly query: string;
  readonly results: readonly SearchClassResult[];
  readonly isLoading: boolean;
  readonly isFetching: boolean;
  readonly isError: boolean;
}

/**
 * Debounced React Query hook for the class (board) typeahead endpoint
 * (`GET /midl/api/v3/board/typeahead?code=…`).
 */
export function useClassTypeaheadQuery({
  query,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  enabled = true,
}: UseClassTypeaheadOptions): UseClassTypeaheadResult {
  const debounced = useDebouncedValue(query.trim(), debounceMs);
  const isQueryable = debounced.length >= MIN_QUERY_LENGTH;

  const queryResult = useQuery({
    queryKey: queryKeys.search.classes(debounced),
    queryFn: ({ signal }) => searchClassesTypeahead({ query: debounced, signal }),
    enabled: enabled && isQueryable,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });

  return {
    query: debounced,
    results: isQueryable ? (queryResult.data ?? []) : [],
    isLoading: isQueryable && queryResult.isLoading,
    isFetching: isQueryable && queryResult.isFetching,
    isError: queryResult.isError,
  };
}
